import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ChevronRight, ChevronLeft, Search, Plus, Trash2, Package,
  Edit3, GripVertical, CheckCircle2, AlertTriangle, FileSignature,
} from 'lucide-react';

/**
 * Product Catalog proposal wizard — Stage 1
 *
 * 4-step flow:  A Basics → B Items → C Terms → D Review
 * Same Soft-Glass visual language as the existing New Proposal page.
 */

const UNIT_LABELS = {
  per_month: 'month', per_week: 'week', per_day: 'day', per_hour: 'hour',
  per_post: 'post',   per_item: 'item',  one_time: 'one-time',
};

const STEPS = [
  { key: 'basics',  label: 'Basics' },
  { key: 'items',   label: 'Items' },
  { key: 'terms',   label: 'Terms' },
  { key: 'review',  label: 'Review' },
];

function fmtINR(n) {
  if (n == null || n === '') return '—';
  try { return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`; }
  catch { return `₹${n}`; }
}

function amountInWords(n) {
  // Simple Indian numbering lakhs/crores words — good enough for Stage 1
  // display; Stage 2's PDF layer will reuse the backend `words_inr`.
  const num = Math.floor(Number(n || 0));
  if (!num) return 'Zero rupees only';
  return `${Number(num).toLocaleString('en-IN')} rupees only`;
}


function emptyLine() {
  return {
    service_key: 'product_catalog_custom',
    product_id: null,
    item: '',
    description: '',
    original_description: '',  // keeps catalog default for "edited" flag
    unit_type: 'one_time',
    rate: 0,
    original_rate: null,       // keeps catalog rate for "differs" badge
    quantity: 1,
    duration: 1,
    gst_pct: 18,
    _edited_desc: false,
  };
}


export default function NewProductCatalogProposalPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [products, setProducts] = useState([]);
  const [productQuery, setProductQuery] = useState('');
  const [productCat, setProductCat] = useState('all');
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [generalTerms, setGeneralTerms] = useState([]);  // from server, "product_catalog" terms kind
  const [draft, setDraft] = useState({
    proposal_type: 'product_catalog',
    client_id: null, client_name: '', contact_name: '', contact_email: '', contact_phone: '',
    subject_line: 'Proposal for Services',
    validity_date: '', start_date: '',
    notes: '',
    discount_pct: 0,
    lines: [],
    conditions_snapshot: [],  // final merged list the salesperson edits in step C
    prepared_by: {
      name: user?.name || '', phone: user?.phone || '',
      email: user?.email || '', designation: user?.role || '',
    },
  });

  useEffect(() => {
    (async () => {
      try {
        const [prods, cats, cls] = await Promise.all([
          api.get('/catalog/products/active'),
          api.get('/catalog/categories'),
          api.get('/clients').catch(() => ({ data: [] })),
        ]);
        setProducts(prods.data || []);
        setCategories(cats.data || []);
        setClients(cls.data?.rows || cls.data || []);
      } catch (e) {
        toast.error('Could not load catalog');
      }
      try {
        // General terms for the product_catalog kind. Day-1 is empty (draft).
        const t = await api.get('/proposals/terms-versions', {
          params: { kind: 'product_catalog', current: true },
        });
        const current = (t.data || []).find(v => v.is_current) || null;
        if (current?.body) {
          const clauses = String(current.body).split(/\n\s*\n/).map((c, i) => ({
            id: `gen-${i}`, order: i + 1, text: c.trim(),
            source: 'master', included: true, kind: 'general',
          })).filter(c => c.text);
          setGeneralTerms(clauses);
        }
      } catch { /* day-1 empty is OK */ }
    })();
  }, []);

  const filteredProducts = useMemo(() => {
    let rows = products;
    if (productCat !== 'all') rows = rows.filter(p => p.category === productCat);
    if (productQuery) {
      const q = productQuery.toLowerCase();
      rows = rows.filter(p => (p.name + ' ' + (p.default_description || '')).toLowerCase().includes(q));
    }
    return rows;
  }, [products, productCat, productQuery]);

  /* ─────────── Line management ─────────── */
  const addFromCatalog = (p) => {
    const line = {
      service_key: 'product_catalog_item',
      product_id: p.id,
      item: p.name,
      description: p.default_description || '',
      original_description: p.default_description || '',
      unit_type: p.unit_type,
      rate: Number(p.default_rate),
      original_rate: Number(p.default_rate),
      quantity: 1,
      duration: p.unit_type === 'one_time' ? 1 : 1,
      gst_pct: Number(p.gst_pct ?? 18),
      _product_terms: p.product_terms || [],
      _edited_desc: false,
    };
    setDraft(d => ({ ...d, lines: [...d.lines, line] }));
    toast.success(`${p.name} added`);
  };
  const addCustom = () => {
    setDraft(d => ({ ...d, lines: [...d.lines, emptyLine()] }));
  };
  const patchLine = (idx, patch) => {
    setDraft(d => {
      const next = [...d.lines];
      next[idx] = { ...next[idx], ...patch };
      if ('description' in patch) {
        next[idx]._edited_desc = (patch.description || '') !== (next[idx].original_description || '');
      }
      return { ...d, lines: next };
    });
  };
  const removeLine = (idx) => {
    setDraft(d => {
      const next = [...d.lines];
      next.splice(idx, 1);
      return { ...d, lines: next };
    });
  };
  const moveLine = (idx, dir) => {
    setDraft(d => {
      const next = [...d.lines];
      const t = idx + dir;
      if (t < 0 || t >= next.length) return d;
      [next[idx], next[t]] = [next[t], next[idx]];
      return { ...d, lines: next };
    });
  };

  /* ─────────── Terms merge (general + per-product, no duplicates) ─────────── */
  const mergedTerms = useMemo(() => {
    const list = [...generalTerms];
    // Group product terms under a heading per product name (first occurrence
    // only; same product reused ⇒ terms still shown once).
    const seenProducts = new Set();
    const seenTexts = new Set(generalTerms.map(c => (c.text || '').trim()));
    for (const ln of draft.lines) {
      const prodId = ln.product_id || `custom:${ln.item}`;
      if (seenProducts.has(prodId)) continue;
      seenProducts.add(prodId);
      const terms = ln._product_terms || [];
      if (!terms.length) continue;
      list.push({
        id: `h-${prodId}`, order: list.length + 1,
        text: `— ${ln.item} —`, source: 'heading', included: true, kind: 'heading',
      });
      for (const t of terms) {
        const txt = (t.text || '').trim();
        if (!txt || seenTexts.has(txt)) continue;
        seenTexts.add(txt);
        list.push({
          id: `pt-${prodId}-${t.id}`, order: list.length + 1,
          text: txt, source: 'product', included: true, kind: 'product',
          product_id: ln.product_id, product_name: ln.item,
        });
      }
    }
    return list;
  }, [generalTerms, draft.lines]);

  // Re-materialise conditions_snapshot when items change so step C can edit it.
  useEffect(() => {
    setDraft(d => {
      // Preserve user edits on terms that still exist by matching id.
      const prev = d.conditions_snapshot || [];
      const byId = Object.fromEntries(prev.map(c => [c.id, c]));
      const next = mergedTerms.map((t, i) => ({
        ...t,
        order: i + 1,
        text: byId[t.id]?._user_edited ? byId[t.id].text : t.text,
        included: byId[t.id]?.included ?? t.included,
        _user_edited: byId[t.id]?._user_edited || false,
      }));
      return { ...d, conditions_snapshot: next };
    });
  }, [mergedTerms]); // eslint-disable-line

  /* ─────────── Totals ─────────── */
  const totals = useMemo(() => {
    let subtotal = 0, gst = 0;
    const breakdown = {};
    for (const ln of draft.lines) {
      const qty = Number(ln.quantity || 1);
      const rate = Number(ln.rate || 0);
      const dur = Number(ln.duration || 1) || 1;
      const amount = qty * rate * dur;
      subtotal += amount;
      const pct = Number(ln.gst_pct ?? 18);
      const taxable = amount * (1 - Number(draft.discount_pct || 0) / 100);
      const g = +(taxable * pct / 100).toFixed(2);
      gst += g;
      if (!breakdown[pct]) breakdown[pct] = { pct, taxable: 0, gst: 0 };
      breakdown[pct].taxable += taxable;
      breakdown[pct].gst += g;
    }
    const discount = +(subtotal * Number(draft.discount_pct || 0) / 100).toFixed(2);
    const taxable = +(subtotal - discount).toFixed(2);
    const total = +(taxable + gst).toFixed(2);
    return {
      subtotal: +subtotal.toFixed(2), discount, taxable,
      gst_amount: +gst.toFixed(2), total,
      breakdown: Object.values(breakdown).map(b => ({
        pct: b.pct, taxable: +b.taxable.toFixed(2), gst: +b.gst.toFixed(2),
      })).sort((a, b) => a.pct - b.pct),
    };
  }, [draft.lines, draft.discount_pct]);

  /* ─────────── Step validation ─────────── */
  const canProceed = useMemo(() => {
    if (step === 0) {
      return !!(draft.client_name && draft.validity_date
                && draft.prepared_by.name && draft.prepared_by.phone);
    }
    if (step === 1) {
      return draft.lines.length > 0 && draft.lines.every(l => l.item && Number(l.rate) >= 0);
    }
    return true;
  }, [step, draft]);

  const next = () => {
    if (!canProceed) return toast.error('Fill the required fields first');
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep(s => Math.max(s - 1, 0));

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        ...draft,
        lines: draft.lines.map(l => ({
          service_key: l.service_key,
          product_id: l.product_id,
          item: l.item,
          description: l.description,
          original_description: l.original_description,
          unit_type: l.unit_type,
          rate: Number(l.rate || 0),
          original_rate: l.original_rate,
          quantity: Number(l.quantity || 1),
          duration: Number(l.duration || 1),
          gst_pct: Number(l.gst_pct ?? 18),
          description_edited: !!l._edited_desc,
          product_terms_snapshot: l._product_terms || [],
        })),
      };
      const { data } = await api.post('/proposals', body);
      toast.success(`Draft saved as ${data.number}`);
      navigate(`/proposals/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save draft');
    } finally { setBusy(false); }
  };

  /* ─────────── Render ─────────── */
  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6 space-y-5"
         data-testid="catalog-wizard">
      {/* Breadcrumb + title */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate('/proposals/new')}
                className="text-xs text-ink-2 inline-flex items-center gap-1 hover:text-ink"
                data-testid="catalog-wizard-back">
          <ChevronLeft className="h-3 w-3" /> Pick a different type
        </button>
        <div className="text-xs text-ink-2 inline-flex items-center gap-1">
          <Package className="h-3.5 w-3.5" /> Product Catalog proposal
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 text-xs" data-testid="catalog-stepper">
        {STEPS.map((s, i) => (
          <button key={s.key} onClick={() => setStep(i)}
                  className={`px-3 py-1.5 rounded-full border transition ${
                    i === step ? 'bg-ink text-white border-ink'
                    : i < step ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-slate-50 text-ink-2 border-slate-200'
                  }`}
                  data-testid={`catalog-step-${s.key}`}>
            {i + 1}. {s.label}
          </button>
        ))}
      </div>

      {step === 0 && (
        <StepBasics draft={draft} setDraft={setDraft} clients={clients} />
      )}
      {step === 1 && (
        <StepItems
          draft={draft} patchLine={patchLine} removeLine={removeLine} moveLine={moveLine}
          addCustom={addCustom} addFromCatalog={addFromCatalog}
          products={filteredProducts} productQuery={productQuery} setProductQuery={setProductQuery}
          productCat={productCat} setProductCat={setProductCat} categories={categories}
        />
      )}
      {step === 2 && (
        <StepTerms draft={draft} setDraft={setDraft} />
      )}
      {step === 3 && (
        <StepReview draft={draft} setDraft={setDraft} totals={totals} />
      )}

      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" onClick={back} disabled={step === 0}
                data-testid="catalog-nav-back">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={next} disabled={!canProceed}
                  data-testid="catalog-nav-next">
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={save} disabled={busy || draft.lines.length === 0}
                  data-testid="catalog-nav-save">
            {busy ? 'Saving…' : 'Save proposal'}
          </Button>
        )}
      </div>
    </div>
  );
}


/* ──────────────── Step A: Basics ──────────────── */
function StepBasics({ draft, setDraft, clients }) {
  return (
    <Card className="shine-card p-5 rounded-xl space-y-4" data-testid="catalog-step-basics-card">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>Client or lead <span className="text-rose-600">*</span></Label>
          <Input list="catalog-client-options" value={draft.client_name}
                 onChange={(e) => {
                   const name = e.target.value;
                   const match = clients.find(c => (c.name || c.company || '') === name);
                   setDraft(d => ({
                     ...d, client_name: name,
                     client_id: match?.id || null,
                     contact_email: match?.primary_contact_email || match?.email || d.contact_email,
                     contact_phone: match?.primary_contact_phone || match?.phone || d.contact_phone,
                     contact_name:  match?.primary_contact_name  || match?.contact_name || d.contact_name,
                   }));
                 }}
                 placeholder="Type to pick an existing client or enter a new name"
                 data-testid="catalog-field-client" />
          <datalist id="catalog-client-options">
            {clients.slice(0, 200).map(c => (
              <option key={c.id} value={c.name || c.company || ''} />
            ))}
          </datalist>
        </div>
        <div>
          <Label>Contact name</Label>
          <Input value={draft.contact_name}
                 onChange={(e) => setDraft(d => ({ ...d, contact_name: e.target.value }))} />
        </div>
        <div>
          <Label>Contact email</Label>
          <Input type="email" value={draft.contact_email}
                 onChange={(e) => setDraft(d => ({ ...d, contact_email: e.target.value }))} />
        </div>
        <div>
          <Label>Subject on PDF</Label>
          <Input value={draft.subject_line}
                 onChange={(e) => setDraft(d => ({ ...d, subject_line: e.target.value }))} />
        </div>
        <div>
          <Label>Valid till <span className="text-rose-600">*</span></Label>
          <Input type="date" value={draft.validity_date}
                 onChange={(e) => setDraft(d => ({ ...d, validity_date: e.target.value }))}
                 data-testid="catalog-field-validity" />
        </div>
        <div>
          <Label>Start date (optional)</Label>
          <Input type="date" value={draft.start_date}
                 onChange={(e) => setDraft(d => ({ ...d, start_date: e.target.value }))} />
        </div>
        <div className="col-span-2">
          <Label>Notes to client (shown on the PDF above the table)</Label>
          <Textarea rows={2} value={draft.notes}
                    onChange={(e) => setDraft(d => ({ ...d, notes: e.target.value }))}
                    data-testid="catalog-field-notes" />
        </div>
      </div>
      <div className="border-t border-ink/10 pt-4 grid grid-cols-2 gap-4">
        <div>
          <Label>Your name <span className="text-rose-600">*</span></Label>
          <Input value={draft.prepared_by.name}
                 onChange={(e) => setDraft(d => ({
                   ...d, prepared_by: { ...d.prepared_by, name: e.target.value },
                 }))}
                 data-testid="catalog-field-prep-name" />
        </div>
        <div>
          <Label>Your phone <span className="text-rose-600">*</span></Label>
          <Input value={draft.prepared_by.phone}
                 onChange={(e) => setDraft(d => ({
                   ...d, prepared_by: { ...d.prepared_by, phone: e.target.value },
                 }))}
                 data-testid="catalog-field-prep-phone" />
        </div>
      </div>
    </Card>
  );
}


/* ──────────────── Step B: Items ──────────────── */
function StepItems({ draft, patchLine, removeLine, moveLine, addCustom, addFromCatalog,
                    products, productQuery, setProductQuery, productCat, setProductCat,
                    categories }) {
  return (
    <div className="space-y-4" data-testid="catalog-step-items-card">
      <Card className="shine-card p-4 rounded-xl">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="h-4 w-4 text-ink-2 absolute left-2.5 top-2.5" />
            <Input placeholder="Search products" value={productQuery}
                   onChange={(e) => setProductQuery(e.target.value)}
                   className="pl-8" data-testid="catalog-items-search" />
          </div>
          <Select value={productCat} onValueChange={setProductCat}>
            <SelectTrigger className="w-[200px]" data-testid="catalog-items-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map(c => (
                <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={addCustom} data-testid="catalog-add-custom-btn">
            <Plus className="h-4 w-4 mr-1" /> Custom item
          </Button>
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[220px] overflow-y-auto">
          {products.length === 0 ? (
            <div className="col-span-2 text-xs text-ink-2 italic py-4 text-center">
              No products available. Owner / Admin can add some in Rate Catalog → Product Catalog.
            </div>
          ) : products.map(p => (
            <button key={p.id} onClick={() => addFromCatalog(p)}
                    className="text-left p-2 border border-ink/10 rounded-lg hover:border-indigo-300 hover:bg-indigo-50/30 transition"
                    data-testid={`catalog-add-product-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm text-ink">{p.name}</div>
                <div className="text-xs text-ink-2">{fmtINR(p.default_rate)} / {UNIT_LABELS[p.unit_type]}</div>
              </div>
              <div className="text-[11px] text-ink-2">{p.category} · GST {p.gst_pct}%</div>
            </button>
          ))}
        </div>
      </Card>

      {/* Line editor */}
      {draft.lines.length === 0 ? (
        <Card className="shine-card p-8 rounded-xl text-center text-sm text-ink-2"
              data-testid="catalog-items-empty">
          Pick products above or add a custom item to begin.
        </Card>
      ) : (
        <Card className="shine-card rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-ink-2 uppercase border-b border-ink/10">
                  <th className="py-2 px-3 text-left">#</th>
                  <th className="py-2 px-3 text-left">Item &amp; description</th>
                  <th className="py-2 px-3">Unit</th>
                  <th className="py-2 px-3 text-right">Rate</th>
                  <th className="py-2 px-3 text-right">Qty</th>
                  <th className="py-2 px-3 text-right">Duration</th>
                  <th className="py-2 px-3 text-right">GST %</th>
                  <th className="py-2 px-3 text-right">Amount</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {draft.lines.map((ln, i) => {
                  const amount = Number(ln.rate || 0) * Number(ln.quantity || 1) * (Number(ln.duration || 1) || 1);
                  const rateDiffers = ln.original_rate != null && Number(ln.original_rate) !== Number(ln.rate);
                  return (
                    <tr key={i} className="border-b border-ink/5 align-top">
                      <td className="py-2 px-3 text-xs text-ink-2">
                        {i + 1}
                        <div className="flex flex-col mt-1">
                          <button type="button" onClick={() => moveLine(i, -1)} className="text-ink-2 hover:text-ink">▲</button>
                          <button type="button" onClick={() => moveLine(i, 1)} className="text-ink-2 hover:text-ink">▼</button>
                        </div>
                      </td>
                      <td className="py-2 px-3 space-y-1 min-w-[260px]">
                        <Input value={ln.item} placeholder="Item name"
                               onChange={(e) => patchLine(i, { item: e.target.value })}
                               data-testid={`catalog-line-item-${i}`} />
                        <Textarea rows={2} value={ln.description} placeholder="Description shown to the client"
                                  onChange={(e) => patchLine(i, { description: e.target.value })}
                                  data-testid={`catalog-line-desc-${i}`} />
                        {ln._edited_desc && (
                          <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50 text-[10px]">
                            <Edit3 className="h-2.5 w-2.5 mr-1" /> Description edited
                          </Badge>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <Select value={ln.unit_type} onValueChange={(v) => patchLine(i, { unit_type: v })}>
                          <SelectTrigger className="w-[110px]" data-testid={`catalog-line-unit-${i}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(UNIT_LABELS).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-2 px-3 text-right">
                        <Input type="number" min="0" step="0.01" className="w-[100px] text-right"
                               value={ln.rate}
                               onChange={(e) => patchLine(i, { rate: e.target.value })}
                               data-testid={`catalog-line-rate-${i}`} />
                        {rateDiffers && (
                          <div className="text-[10px] text-ink-2 mt-0.5">
                            catalog {fmtINR(ln.original_rate)}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <Input type="number" min="1" className="w-[70px] text-right"
                               value={ln.quantity}
                               onChange={(e) => patchLine(i, { quantity: e.target.value })}
                               data-testid={`catalog-line-qty-${i}`} />
                      </td>
                      <td className="py-2 px-3 text-right">
                        {ln.unit_type === 'one_time' ? (
                          <span className="text-xs text-ink-2">—</span>
                        ) : (
                          <Input type="number" min="1" className="w-[70px] text-right"
                                 value={ln.duration}
                                 onChange={(e) => patchLine(i, { duration: e.target.value })}
                                 data-testid={`catalog-line-dur-${i}`} />
                        )}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <Input type="number" min="0" max="28" step="0.01" className="w-[70px] text-right"
                               value={ln.gst_pct}
                               onChange={(e) => patchLine(i, { gst_pct: e.target.value })}
                               data-testid={`catalog-line-gst-${i}`} />
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-ink">
                        {fmtINR(amount)}
                      </td>
                      <td className="py-2 px-3">
                        <Button variant="ghost" size="sm" onClick={() => removeLine(i)}
                                data-testid={`catalog-line-remove-${i}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}


/* ──────────────── Step C: Terms ──────────────── */
function StepTerms({ draft, setDraft }) {
  const list = draft.conditions_snapshot || [];
  const update = (idx, patch) => {
    setDraft(d => {
      const next = [...(d.conditions_snapshot || [])];
      next[idx] = { ...next[idx], ...patch };
      if ('text' in patch) next[idx]._user_edited = true;
      return { ...d, conditions_snapshot: next };
    });
  };
  const toggle = (idx) => update(idx, { included: !list[idx].included });
  const remove = (idx) => {
    setDraft(d => {
      const next = [...(d.conditions_snapshot || [])];
      next.splice(idx, 1);
      return { ...d, conditions_snapshot: next };
    });
  };
  const add = () => {
    setDraft(d => ({
      ...d, conditions_snapshot: [
        ...(d.conditions_snapshot || []),
        { id: `custom-${Date.now()}`, order: list.length + 1,
          text: '', source: 'custom', included: true, kind: 'custom' },
      ],
    }));
  };

  return (
    <Card className="shine-card p-5 rounded-xl" data-testid="catalog-step-terms-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-semibold text-ink text-sm">Terms &amp; conditions</div>
          <div className="text-xs text-ink-2">
            General terms first, then product-specific clauses under their headings.
            Edits here affect this proposal only.
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={add} data-testid="catalog-term-add">
          <Plus className="h-4 w-4 mr-1" /> Add clause
        </Button>
      </div>
      {list.length === 0 ? (
        <div className="text-xs text-ink-2 italic py-4">
          No clauses yet. Add some, or set up general terms in Rate Catalog → Terms.
        </div>
      ) : (
        <ol className="space-y-2">
          {list.map((c, i) => (
            c.kind === 'heading' ? (
              <li key={c.id} className="pt-3 font-semibold text-sm text-ink">{c.text}</li>
            ) : (
              <li key={c.id} className="flex items-start gap-2">
                <input type="checkbox" checked={c.included} onChange={() => toggle(i)}
                       className="mt-2" data-testid={`catalog-term-included-${i}`} />
                <span className="text-xs text-ink-2 pt-2 w-6 text-right">{i + 1}.</span>
                <Textarea rows={2} value={c.text} onChange={(e) => update(i, { text: e.target.value })}
                          data-testid={`catalog-term-text-${i}`} />
                <Button variant="ghost" size="sm" onClick={() => remove(i)}
                        data-testid={`catalog-term-remove-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            )
          ))}
        </ol>
      )}
    </Card>
  );
}


/* ──────────────── Step D: Review ──────────────── */
function StepReview({ draft, setDraft, totals }) {
  return (
    <div className="space-y-4" data-testid="catalog-step-review-card">
      <Card className="shine-card p-5 rounded-xl space-y-3">
        <div className="font-semibold text-ink text-sm">Items ({draft.lines.length})</div>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-ink-2 uppercase border-b border-ink/10">
              <th className="py-1.5 text-left">#</th>
              <th className="py-1.5 text-left">Item</th>
              <th className="py-1.5 text-right">Rate</th>
              <th className="py-1.5 text-right">Qty</th>
              <th className="py-1.5 text-right">Duration</th>
              <th className="py-1.5 text-right">GST</th>
              <th className="py-1.5 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {draft.lines.map((ln, i) => {
              const amt = Number(ln.rate || 0) * Number(ln.quantity || 1) * (Number(ln.duration || 1) || 1);
              return (
                <tr key={i} className="border-b border-ink/5">
                  <td className="py-1.5 text-ink-2">{i + 1}</td>
                  <td className="py-1.5">
                    <div className="font-semibold text-ink">{ln.item}</div>
                    {ln.description && (
                      <div className="text-[11px] text-ink-2 whitespace-pre-wrap">{ln.description}</div>
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{fmtINR(ln.rate)}</td>
                  <td className="py-1.5 text-right">{ln.quantity}</td>
                  <td className="py-1.5 text-right">{ln.unit_type === 'one_time' ? '—' : ln.duration}</td>
                  <td className="py-1.5 text-right">{ln.gst_pct}%</td>
                  <td className="py-1.5 text-right tabular-nums">{fmtINR(amt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Card className="shine-card p-5 rounded-xl grid grid-cols-2 gap-6" data-testid="catalog-review-totals">
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-ink-2">Subtotal</span>
            <span className="font-semibold text-ink">{fmtINR(totals.subtotal)}</span>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Extra discount %</Label>
              <Input type="number" min="0" max="100" step="0.1" className="w-[90px] text-right"
                     value={draft.discount_pct}
                     onChange={(e) => setDraft(d => ({ ...d, discount_pct: e.target.value }))}
                     data-testid="catalog-review-discount" />
            </div>
            {Number(draft.discount_pct) > 0 && (
              <div className="flex items-center justify-between text-xs text-ink-2 mt-1">
                <span>Extra discount ({draft.discount_pct}%)</span>
                <span>− {fmtINR(totals.discount)}</span>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between border-t border-ink/10 pt-2">
            <span className="text-ink-2">Taxable</span>
            <span className="tabular-nums">{fmtINR(totals.taxable)}</span>
          </div>
          <div className="space-y-1">
            {totals.breakdown.map(b => (
              <div key={b.pct} className="flex items-center justify-between text-xs text-ink-2">
                <span>GST @ {b.pct}% on {fmtINR(b.taxable)}</span>
                <span className="tabular-nums">+ {fmtINR(b.gst)}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-ink/10 pt-3 text-base">
            <span className="font-semibold text-ink">Total</span>
            <span className="font-bold text-ink tabular-nums">{fmtINR(totals.total)}</span>
          </div>
          <div className="text-xs text-ink-2 italic pt-1">{amountInWords(totals.total)}</div>
        </div>

        <div className="space-y-2 text-sm">
          <div className="font-semibold text-ink">What happens next</div>
          <div className="text-xs text-ink-2 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5" />
            After saving you can Generate and Send it straight away, and edit it any time.
          </div>
          <div className="text-xs text-ink-2 flex items-start gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5" />
            Prepared by <strong>{draft.prepared_by.name || '—'}</strong> ·
            {' '}{draft.prepared_by.phone || '—'}
          </div>
        </div>
      </Card>
    </div>
  );
}
